import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from './baseQuerySlice';
import {
  AgentStatusList,
  AgentMarketPlaceItemWithFilter,
  AgentStatusItem,
  startAgentResponse,
} from '../../pages/userAgent/agentMarketPlace/AgentMarketPlace';
import { DeveloperKey } from '../../shared/developerKey/DeveloperKey';
import { DownloadAgentParams, IngestImagesParams } from '../../pages/userAgent/promtChat/PromtChat';

export const apiService = createApi({
  reducerPath: 'apiService',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['AgentsList', 'DeveloperKey', 'AgentStatus'], // Define the tag types
  endpoints: (builder) => ({
    // Check if user is admin
    isAdmin: builder.query<{ is_admin: boolean }, void>({
      query: () => ({
        url: '/script_agent_orchestrator/v1/agent/is-admin',
        method: 'GET',
      }),
    }),
    getAgentReviews: builder.query({
      query: (agentId) => ({
        url: `/script_agent_orchestrator/v1/agent/${agentId}/review`,
        method: 'GET',
      }),
    }),

    postAgentReview: builder.mutation({
      query: ({ agentId, payload }) => ({
        url: `/script_agent_orchestrator/v1/agent/${agentId}/review`,
        method: 'POST',
        body: payload,
      }),
    }),

    putAgentReview: builder.mutation({
      query: ({ reviewId, payload }) => ({
        url: `/script_agent_orchestrator/v1/agent/review/${reviewId}`,
        method: 'PUT',
        body: payload,
      }),
    }),
    putAgentDemoVideo: builder.mutation({
      query: ({ partyId, payload }) => ({
        url: `/script_agent_orchestrator/v1/agent/${partyId}/partial`,
        method: 'PUT',
        body: payload,
      }),
    }),

    // Delete agent review
    deleteAgentReview: builder.mutation<{ status: string; message: string }, string>({
      query: (reviewId) => ({
        url: `/script_agent_orchestrator/v1/agent/review/${reviewId}`,
        method: 'DELETE',
      }),
    }),

    //Get agents list
    getAgentsList: builder.query<AgentMarketPlaceItemWithFilter[], void>({
      // Specify return type and param type
      query: () => ({
        url: '/script_agent_orchestrator/v1/agent/marketplace',
        method: 'GET',
      }),
      providesTags: ['AgentsList'], //Attach tag to query
    }),

    //Get agents status
    getAgentsStatus: builder.query<AgentStatusList, void>({
      // Specify return type and param type
      query: () => ({
        url: '/script_agent_orchestrator/v1/agent/status/all',
        method: 'GET',
      }),
      providesTags: ['AgentStatus'], //Attach tag to query
    }),

    //Start the agent
    startAgent: builder.mutation<startAgentResponse, { party_id: string }>({
      // Specify return type and param type
      query: ({ party_id }) => ({
        url: `/script_agent_orchestrator/v1/agent/start_agent/${party_id}/start_workflow`,
        method: 'GET',
      }),
      invalidatesTags: ['AgentStatus'],
    }),

    //Get the status of particular agent
    getSpecificAgentStatus: builder.mutation<AgentStatusItem, { party_id: string }>({
      // Specify return type and param type
      query: ({ party_id }) => ({
        url: `/script_agent_orchestrator/v1/agent/${party_id}/status`,
        method: 'GET',
      }),
      invalidatesTags: ['AgentStatus'],
    }),

    //Update the agent status
    updateAgentStatus: builder.mutation<AgentStatusItem, { party_id: string }>({
      // Specify return type and param type
      query: ({ party_id }) => ({
        url: `/script_agent_orchestrator/v1/agent/${party_id}/status`,
        method: 'PUT',
        body: { status: 'RUNNING' },
      }),
      invalidatesTags: ['AgentStatus'],
    }),

    // Get developer key
    getDeveloperKey: builder.query<DeveloperKey, void>({
      query: () => ({
        url: '/v1.1/apikey/trial',
        method: 'GET',
      }),
      providesTags: ['DeveloperKey'],
    }),

    // Create developer key
    createDeveloperKey: builder.mutation<DeveloperKey, string>({
      query: (name) => ({
        url: '/v1.1/apikey/trial',
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['DeveloperKey'],
    }),

    // Delete developer key
    deleteDeveloperKey: builder.mutation<void, string>({
      query: (apiKey) => ({
        url: '/v1.1/apikey/trial',
        method: 'DELETE',
        body: { api_key: apiKey },
      }),
      invalidatesTags: ['DeveloperKey'],
    }),

    // Ingest Images mutation
    ingestImages: builder.mutation<any, IngestImagesParams>({
      query: ({ partyId, files }) => {
        // Create FormData and append files
        const formData = new FormData();
        files.forEach((file, index) => {
          formData.append(`file${index}`, file);
        });

        return {
          url: `/v1.1/agent_chat_session/script_agent/${partyId}/ingest`,
          method: 'POST',
          body: formData,
          formData: true, // Signal that we're sending FormData
        };
      },
    }),

    // Download Agent mutation
    downloadAgent: builder.mutation<Blob, DownloadAgentParams>({
      query: ({ partyId }) => ({
        url: `/script_agent_orchestrator/v1/agent/${partyId}/download`,
        method: 'GET',
        params: { blob_link: 'true' },
        responseHandler: (response: Response) => response.json(), 
      }),
    }),

    //downlaod md content for downloadable_only flag
    downloadAgentMd: builder.query<string, string>({
      query: (partyId) => ({
        url: `/script_agent_orchestrator/v1/agent/${partyId}/download`,
        method: 'GET',
        params: { readme_only: 'true' },
        responseHandler: (response: Response) => response.text(),
      }),
    }),
    getDataset: builder.query<any, string>({
      query: (datasetId) => ({
        url: `/v1.1/datasets/${datasetId}`,
        method: 'GET',
      }),
    }),

    // agent finder chatbot endpoint
    sendChatMessage: builder.mutation({
      query: (payload) => ({
        url: '/agent-finder-chatbot-svc/api/chat',
        method: 'POST',
        body: payload,
      }),
    }),

    // Deploy Agent mutation
    deployAgent: builder.mutation<any, { partyId: string; agentFile: File }>({
      query: ({ partyId, agentFile }) => {
        const formData = new FormData();
        formData.append('agent_file', agentFile);

        return {
          url: `/script_agent_orchestrator/v1/agent/deploy/${partyId}`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['AgentsList'],
    }),

    // Edit Agent mutation (PUT)
    editAgent: builder.mutation<any, { partyId: string; agentData: any }>({
      query: ({ partyId, agentData }) => ({
        url: `/script_agent_orchestrator/v1/agent/deploy/${partyId}`,
        method: 'PUT',
        body: agentData,
      }),
      invalidatesTags: ['AgentsList'],
    }),
    // Delete Agent mutation
    deleteAgent: builder.mutation<any, { partyId: string }>({
      query: ({ partyId }) => ({
        url: `/script_agent_orchestrator/v1/agent/deploy/${partyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AgentsList'],
    }),
    // Get all gold certificate agents
    getGoldCertificateList: builder.query<any[], void>({
      query: () => ({
        url: '/script_agent_orchestrator/v1/agent/gold-certificate/all',
        method: 'GET',
      }),
    }),

    // Set or unset gold certificate for an agent (PUT)
    setGoldCertificate: builder.mutation<any, { agentId: string; gold_rating: boolean }>({
      query: ({ agentId, gold_rating }) => ({
        url: `/script_agent_orchestrator/v1/agent/${agentId}/gold-certificate`,
        method: 'PUT',
        body: { gold_rating },
        headers: { 'Content-Type': 'application/json' },
      }),
      // Optionally invalidate gold list if needed
      // invalidatesTags: ['AgentsList'],
    }),

    // Track agent click analytics
    trackAgentClick: builder.mutation<
      any,
      {
        user_id: string;
        user_name: string;
        email: string;
        agent_id: string;
        agent_name: string;
        click_type: string;
      }
    >({
      query: (payload) => ({
        url: '/script_agent_orchestrator/v1/agent/analytics/click',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['AgentsList'], // Refetch agents list to update counts
    }),

    // Record user visit/login
    recordUserVisit: builder.mutation<
      {
        user_id: string;
        user_name: string;
        email: string;
        date: string;
        first_login_at: string;
        is_new: boolean;
      },
      {
        user_id: string;
        user_name: string;
        email: string;
      }
    >({
      query: (payload) => ({
        url: '/script_agent_orchestrator/v1/agent/analytics/visit',
        method: 'POST',
        body: payload,
      }),
    }),

    // Get top agents by metrics
    getTopAgents: builder.query<
      {
        top_enquired: Array<{ name: string; count: number }>;
        top_started: Array<{ name: string; count: number }>;
        top_accessed: Array<{ name: string; count: number }>;
      },
      void
    >({
      query: () => ({
        url: '/script_agent_orchestrator/v1/agent/analytics/top-agents',
        method: 'GET',
      }),
    }),

    // Get analytics stats
    getAnalyticsStats: builder.query<
      {
        unique_users_count: number;
        total_accessed_agents: number;
        total_visits: number;
        active_users_now: number;
      },
      void
    >({
      query: () => ({
        url: '/script_agent_orchestrator/v1/agent/analytics/stats',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useIsAdminQuery,
  useGetAgentsStatusQuery,
  useStartAgentMutation,
  useGetSpecificAgentStatusMutation,
  useUpdateAgentStatusMutation,
  useGetAgentsListQuery,
  useGetDeveloperKeyQuery,
  useCreateDeveloperKeyMutation,
  useDeleteDeveloperKeyMutation,
  useIngestImagesMutation,
  useDownloadAgentMutation,
  useDownloadAgentMdQuery,
  useGetDatasetQuery,
  useGetAgentReviewsQuery,
  usePostAgentReviewMutation,
  usePutAgentReviewMutation,
  useDeleteAgentReviewMutation,
  usePutAgentDemoVideoMutation,
  useSendChatMessageMutation,
  useDeployAgentMutation,
  useEditAgentMutation,
  useDeleteAgentMutation,
  useGetGoldCertificateListQuery,
  useSetGoldCertificateMutation,
  useTrackAgentClickMutation,
  useRecordUserVisitMutation,
  useGetTopAgentsQuery,
  useGetAnalyticsStatsQuery,
} = apiService;
